import React from 'react';
import Icon from 'components/AppIcon';

const MarketSlide = () => {
  const marketSegments = [
    {
      icon: 'User',
      title: 'Individual Clients',
      size: '45M+ people',
      description: 'Individuals seeking legal help for personal matters',
      opportunity: 'High volume, recurring needs'
    },
    {
      icon: 'Building',
      title: 'Small-Medium Law Firms',
      size: '8,000+ firms',
      description: 'Law firms with 1-20 lawyers needing practice management',
      opportunity: 'Primary revenue source'
    },
    {
      icon: 'Briefcase',
      title: 'Corporate Clients',
      size: '500K+ businesses',
      description: 'Companies requiring ongoing legal services',
      opportunity: 'High value contracts'
    },
    {
      icon: 'Heart',
      title: 'Legal Aid Organizations',
      size: '200+ NGOs',
      description: 'Organizations providing pro bono legal services',
      opportunity: 'Social impact partnerships'
    }
  ];

  const marketDrivers = [
    {
      icon: 'TrendingUp',
      title: 'Digital Transformation',
      description: 'Government and private sector embracing digital solutions',
      impact: '80% mobile penetration rate'
    },
    {
      icon: 'Scale',
      title: 'Legal System Modernization',
      description: 'Courts adopting e-filing and digital processes',
      impact: 'Increased efficiency demands'
    },
    {
      icon: 'Users',
      title: 'Growing Middle Class',
      description: 'Increased demand for legal services',
      impact: '15% annual growth in legal service demand'
    },
    {
      icon: 'Globe',
      title: 'Economic Growth',
      description: 'Kenya as East African business hub',
      impact: 'More commercial legal needs'
    }
  ];

  return (
    <div className="py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-bold text-text-primary mb-4">
            Market Opportunity
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Kenya's legal services market presents a massive opportunity for digital transformation
          </p>
        </div>

        {/* Market Size */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-6 text-center">
            Total Addressable Market
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">KES 25B+</div>
              <p className="text-text-secondary">Annual legal services market</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">15,000+</div>
              <p className="text-text-secondary">Practicing lawyers</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">50M+</div>
              <p className="text-text-secondary">Potential clients</p>
            </div>
          </div>
        </div>

        {/* Market Segments */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Target Market Segments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketSegments?.map((segment, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border hover:shadow-lg transition-smooth">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={segment?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                  {segment?.title}
                </h3>
                <div className="text-sm font-medium text-primary mb-2">
                  {segment?.size}
                </div>
                <p className="text-text-secondary text-sm mb-3">
                  {segment?.description}
                </p>
                <div className="inline-flex items-center px-3 py-1 bg-success-50 text-success-600 rounded-full text-xs font-medium">
                  {segment?.opportunity}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Market Drivers */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-8 text-center">
            Market Growth Drivers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {marketDrivers?.map((driver, index) => (
              <div key={index} className="bg-surface rounded-lg p-6 shadow-card border border-border">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-success-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={driver?.icon} size={24} className="text-success-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-heading font-semibold text-text-primary mb-2">
                      {driver?.title}
                    </h3>
                    <p className="text-text-secondary mb-3">
                      {driver?.description}
                    </p>
                    <div className="text-sm font-medium text-success-600">
                      {driver?.impact}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Competitive Landscape */}
        <div className="bg-warning-50 rounded-lg p-8">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-6 text-center">
            Competitive Advantage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-4">
                Limited Direct Competition
              </h3>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>Most solutions are international, not Kenya-specific</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>Few platforms integrate with Kenyan legal system</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>No comprehensive lawyer-client matching platform</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-4">
                First-Mover Advantage
              </h3>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>Opportunity to capture significant market share</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>Build strong relationships with legal community</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={16} className="text-success-600" />
                  <span>Establish platform as industry standard</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketSlide;